import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import clsx from 'clsx';
import OpportunityEditor from '../components/OpportunityEditor.jsx';
import { useAuth } from '../auth/AuthContext.jsx';

const ADMIN_ROLES = ['superadmin', 'dealer_admin', 'branch_admin'];

// CR 2026-08-05 (John): pipeline flow reordered to Lead > Prospect > Negotiating
// > Qualified > Quoted, and per-stage probability is no longer shown on the board.
const STAGES = [
  { id: 'lead', label: 'Lead', color: 'bg-slate-50' },
  { id: 'prospect', label: 'Prospect', color: 'bg-panel' },
  { id: 'negotiating', label: 'Negotiating', color: 'bg-emerald-50' },
  { id: 'qualified', label: 'Qualified', color: 'bg-blue-50' },
  { id: 'quoted', label: 'Quoted', color: 'bg-amber-50' },
];

const DOMAINS = [
  { id: '', label: 'All departments' },
  { id: 'parts', label: 'Parts' },
  { id: 'other', label: 'Other' },
];

function fmt(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1000) return '$' + Math.round(n).toLocaleString();
  return '$' + n.toFixed(0);
}

export default function Pipeline() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  // Location + salesperson filters. Only meaningful for admins — a sales rep's
  // pipeline is already scoped to their own opportunities server-side.
  const [branchId, setBranchId] = useState('');
  const [userId, setUserId] = useState('');
  // Department filter (Parts / Other): useful to every role, unlike
  // branch/salesperson which are admin-only.
  const [domain, setDomain] = useState('');
  // Open deals render as the kanban below; closed (won/lost) deals get their
  // own list — they used to just vanish once a stage moved to won/lost since
  // the kanban only ever fetched status=open and had no columns for them.
  const [view, setView] = useState('open');
  // Won/Lost sub-filter — only meaningful within the closed view. 'all' fetches
  // both stages (status=closed); 'won'/'lost' narrow to a single stage.
  const [closedFilter, setClosedFilter] = useState('all');
  const filterParams = {};
  if (branchId) filterParams.branch_id = branchId;
  if (userId) filterParams.user_id = userId;
  if (domain) filterParams.domain = domain;

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api.get('/branches').then(r => r.data.data),
    enabled: isAdmin,
  });
  const salespersons = useQuery({
    queryKey: ['opportunities', 'salespersons'],
    queryFn: () => api.get('/opportunities/salespersons').then(r => r.data.data),
    enabled: isAdmin,
  });

  const list = useQuery({
    queryKey: ['opportunities', 'open', branchId, userId, domain],
    queryFn: () => api.get('/opportunities', { params: { status: 'open', ...filterParams } }).then(r => r.data.data),
    enabled: view === 'open',
  });
  const closedList = useQuery({
    queryKey: ['opportunities', 'closed', closedFilter, branchId, userId, domain],
    queryFn: () => api.get('/opportunities', {
      params: { status: closedFilter === 'all' ? 'closed' : closedFilter, ...filterParams },
    }).then(r => r.data.data),
    enabled: view === 'closed',
  });
  const forecast = useQuery({
    queryKey: ['opportunities', 'forecast', branchId, userId, domain],
    queryFn: () => api.get('/opportunities/forecast', { params: filterParams }).then(r => r.data.data),
  });
  const stale = useQuery({
    queryKey: ['opportunities', 'quotes-outstanding', branchId, userId, domain],
    queryFn: () => api.get('/opportunities/quotes-outstanding', { params: { stale_days: 30, ...filterParams } }).then(r => r.data),
  });
  const [editing, setEditing] = useState(null); // opportunity object or 'new'

  const totalWeighted = (forecast.data?.open_by_stage || [])
    .reduce((s, r) => s + Number(r.weighted_value || 0), 0);
  const totalOpen = (forecast.data?.open_by_stage || [])
    .reduce((s, r) => s + Number(r.total_value || 0), 0);

  const wonRecent = forecast.data?.recent?.find(r => r.stage === 'won') || { count: 0, total_value: 0 };
  const lostRecent = forecast.data?.recent?.find(r => r.stage === 'lost') || { count: 0, total_value: 0 };
  const lostPartsRecent = forecast.data?.recent_by_domain?.find(r => r.stage === 'lost' && r.domain === 'parts') || { count: 0, total_value: 0 };

  const byStage = {};
  for (const o of (list.data || [])) {
    (byStage[o.stage] = byStage[o.stage] || []).push(o);
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <div className="section-eyebrow">Pipeline</div>
          <h1 className="text-2xl">Opportunities</h1>
          <div className="rule-crimson" />
        </div>
        <button className="btn-primary" onClick={() => setEditing('new')}>+ New</button>
      </header>

      {/* Open/Closed tabs — closed deals (won/lost) don't belong in the kanban
          (no columns for them), so they get a separate list view instead of
          disappearing once a deal's stage moves to won or lost. */}
      <div className="flex gap-1 border-b border-panelDark">
        <button
          className={clsx('px-3 py-1.5 text-sm font-medium border-b-2 -mb-px',
            view === 'open' ? 'border-crimson text-navy' : 'border-transparent text-muted hover:text-navy')}
          onClick={() => setView('open')}
        >
          Open
        </button>
        <button
          className={clsx('px-3 py-1.5 text-sm font-medium border-b-2 -mb-px',
            view === 'closed' ? 'border-crimson text-navy' : 'border-transparent text-muted hover:text-navy')}
          onClick={() => setView('closed')}
        >
          Won / Lost
        </button>
      </div>

      {/* Won/Lost sub-filter — only shown in the closed view. Defaults to
          'all' (both stages via status=closed); pick a single stage to see
          just wins or just losses. */}
      {view === 'closed' && (
        <div className="flex gap-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'won', label: 'Won' },
            { id: 'lost', label: 'Lost' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setClosedFilter(f.id)}
              className={clsx('text-xs px-2 py-1 rounded font-medium',
                closedFilter === f.id
                  ? f.id === 'won' ? 'bg-emerald-100 text-emerald-800'
                    : f.id === 'lost' ? 'bg-red-100 text-crimson'
                      : 'bg-navy text-white'
                  : 'bg-panel text-muted hover:text-navy')}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Filters — department (all roles), plus location (branch) + salesperson
          (admin-only: a rep already only sees their own pipeline, so per-rep/
          branch filtering is moot for them). */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="input w-auto"
          value={domain}
          onChange={e => setDomain(e.target.value)}
          aria-label="Filter by department"
        >
          {DOMAINS.map(d => (
            <option key={d.id} value={d.id}>{d.label}</option>
          ))}
        </select>
        {isAdmin && (
          <>
            <select
              className="input w-auto"
              value={branchId}
              onChange={e => setBranchId(e.target.value)}
              aria-label="Filter by location"
            >
              <option value="">All locations</option>
              {(branches.data || []).map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <select
              className="input w-auto"
              value={userId}
              onChange={e => setUserId(e.target.value)}
              aria-label="Filter by salesperson"
            >
              <option value="">All salespeople</option>
              {(salespersons.data || []).map(s => (
                <option key={s.id} value={s.id}>{s.full_name}</option>
              ))}
            </select>
          </>
        )}
        {(domain || branchId || userId) && (
          <button className="btn-ghost text-sm" onClick={() => { setDomain(''); setBranchId(''); setUserId(''); }}>
            Clear
          </button>
        )}
      </div>

      {/* Forecast strip -- zeroed stats on a failed fetch would otherwise look
          like a real (if boring) "no pipeline value" signal, so an error here
          gets its own banner instead of silently rendering $0 everywhere. */}
      {forecast.isError ? (
        <div className="card p-3 text-sm text-crimson flex items-center justify-between gap-2">
          <span>Couldn't load pipeline forecast.</span>
          <button className="btn-ghost text-xs" onClick={() => forecast.refetch()}>Retry</button>
        </div>
      ) : forecast.isLoading ? (
        <div className="card p-3 text-muted text-sm">Loading forecast…</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          <Stat label="Open value" value={fmt(totalOpen)} />
          <Stat label="Weighted forecast" value={fmt(totalWeighted)} highlight />
          <Stat label="Won (last 90d)" value={`${wonRecent.count} · ${fmt(wonRecent.total_value)}`} />
          <Stat label="Lost (last 90d)" value={`${lostRecent.count} · ${fmt(lostRecent.total_value)}`} />
          <Stat label="Lost Parts (90d)" value={`${lostPartsRecent.count} · ${fmt(lostPartsRecent.total_value)}`} />
        </div>
      )}

      {/* Stale quotes — open quotes/negotiations not touched in 30+ days */}
      {view === 'open' && (stale.data?.data?.length > 0) && (
        <section className="bg-amber-50 border border-amber-200 rounded p-3">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-sm font-semibold text-amber-900">
              Stale quotes — {stale.data.data.length} not touched in {stale.data.stale_days}+ days
            </div>
            <div className="text-xs text-amber-800">
              Total: {fmt(stale.data.data.reduce((s, r) => s + Number(r.value_usd || 0), 0))}
            </div>
          </div>
          <div className="space-y-1 text-xs">
            {stale.data.data.slice(0, 8).map(o => (
              <button key={o.id} onClick={() => setEditing(o)}
                className="w-full text-left bg-white rounded px-2 py-1 border border-amber-200 hover:ring-1 hover:ring-crimson flex items-center gap-2">
                <span className="flex-1 truncate font-medium">{o.name}</span>
                <span className="text-muted truncate">
                  {o.customer_name
                    ? o.customer_name
                    : (o.customer_name_hint || o.source_customer_name)
                      ? <>{o.customer_name_hint || o.source_customer_name} <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">unlinked</span></>
                      : '—'}
                  {o.customer_account_number && <span className="ml-1 font-mono text-[10px] text-slate-400">#{o.customer_account_number}</span>}
                </span>
                <span className="capitalize text-muted">{o.stage}</span>
                <span className="font-semibold tabular-nums">{fmt(o.value_usd)}</span>
                <span className="text-muted">{o.days_since_update}d</span>
              </button>
            ))}
            {stale.data.data.length > 8 && (
              <div className="text-amber-800 text-[11px] pl-2">…and {stale.data.data.length - 8} more</div>
            )}
          </div>
        </section>
      )}

      {/* Kanban */}
      {view === 'open' && list.isError && (
        <div className="card p-3 text-sm text-crimson flex items-center justify-between gap-2">
          <span>Couldn't load opportunities.</span>
          <button className="btn-ghost text-xs" onClick={() => list.refetch()}>Retry</button>
        </div>
      )}
      {view === 'open' && list.isLoading && (
        <div className="card p-3 text-muted text-sm">Loading opportunities…</div>
      )}
      {view === 'open' && !list.isLoading && !list.isError && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {STAGES.map(s => {
          const items = byStage[s.id] || [];
          const sum = items.reduce((acc, o) => acc + Number(o.value_usd || 0), 0);
          return (
            <div key={s.id} className={clsx('rounded p-2', s.color)}>
              <div className="flex items-baseline justify-between mb-2">
                <div className="font-semibold text-navy text-sm">{s.label}</div>
                <div className="text-xs text-muted">{items.length} · {fmt(sum)}</div>
              </div>
              <div className="space-y-2">
                {items.map(o => (
                  <button key={o.id} onClick={() => setEditing(o)}
                    className="w-full text-left bg-white rounded p-2 border border-panelDark hover:ring-1 hover:ring-crimson">
                    <div className="text-sm font-medium truncate">{o.name}</div>
                    {o.customer_name ? (
                      <div className="text-xs text-muted truncate">
                        {o.customer_name}
                        <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">🔗 linked</span>
                        {o.customer_account_number && (
                          <span className="ml-1 font-mono text-[10px] text-slate-400">#{o.customer_account_number}</span>
                        )}
                      </div>
                    ) : (o.customer_name_hint || o.source_customer_name) ? (
                      <div className="text-xs text-muted truncate">
                        {o.customer_name_hint || o.source_customer_name}
                        <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">unlinked</span>
                      </div>
                    ) : null}
                    {(o.lead_source || (o.tags && o.tags.length > 0)) && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {o.lead_source && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-navy/10 text-navy">{o.lead_source}</span>
                        )}
                        {(o.tags || []).map(t => (
                          <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-panel text-muted">#{t}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-baseline justify-between mt-1 text-xs">
                      <span className="text-navy font-semibold">
                        {fmt(o.value_usd)}
                        {o.cost_usd != null && <span className="text-muted font-normal"> · cost {fmt(o.cost_usd)}</span>}
                      </span>
                      {o.expected_close_date && (
                        <span className="text-muted">{new Date(o.expected_close_date).toLocaleDateString()}</span>
                      )}
                    </div>
                    {o.source_note_date && new Date(o.source_note_date).toDateString() !== new Date(o.created_at).toDateString() && (
                      <div className="text-[10px] text-muted mt-0.5">
                        Activity: {new Date(o.source_note_date).toLocaleDateString()}
                      </div>
                    )}
                  </button>
                ))}
                {items.length === 0 && <div className="text-xs text-muted text-center py-3">No deals</div>}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Closed list — won/lost deals, most recently closed first */}
      {view === 'closed' && closedList.isError && (
        <div className="card p-3 text-sm text-crimson flex items-center justify-between gap-2">
          <span>Couldn't load closed deals.</span>
          <button className="btn-ghost text-xs" onClick={() => closedList.refetch()}>Retry</button>
        </div>
      )}
      {view === 'closed' && closedList.isLoading && (
        <div className="card p-3 text-muted text-sm">Loading closed deals…</div>
      )}
      {view === 'closed' && !closedList.isLoading && !closedList.isError && (
        <div className="space-y-2">
          {(closedList.data || []).length === 0 && (
            <div className="card p-3 text-muted text-sm text-center">
              No {closedFilter === 'all' ? 'won or lost' : closedFilter} deals yet.
            </div>
          )}
          {(closedList.data || []).map(o => (
            <button key={o.id} onClick={() => setEditing(o)}
              className="w-full text-left bg-white rounded p-2 border border-panelDark hover:ring-1 hover:ring-crimson flex items-center gap-3">
              <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0',
                o.stage === 'won' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-crimson')}>
                {o.stage === 'won' ? 'Won' : 'Lost'}
              </span>
              <span className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{o.name}</div>
                <div className="text-xs text-muted truncate">
                  {o.customer_name || o.customer_name_hint || o.source_customer_name || '—'}
                  {o.domain && <span className="capitalize"> · {o.domain.replace('_', ' ')}</span>}
                  {o.stage === 'lost' && o.lost_reason && <span> · {o.lost_reason}</span>}
                </div>
              </span>
              <span className="text-sm font-semibold tabular-nums shrink-0">{fmt(o.value_usd)}</span>
              <span className="text-xs text-muted shrink-0 w-20 text-right">
                {o.closed_at ? new Date(o.closed_at).toLocaleDateString() : '—'}
              </span>
            </button>
          ))}
        </div>
      )}

      {editing && (
        <OpportunityEditor
          opp={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['opportunities'] });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={clsx('card p-3 text-center', highlight && 'ring-1 ring-crimson')}>
      <div className={clsx('text-lg font-bold', highlight ? 'text-crimson' : 'text-navy')}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted">{label}</div>
    </div>
  );
}

